import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, User } from '../types';
import { getPublicShowcase } from '../api';
import type { ShowcaseItem } from '../types';
import { tosThumbnail } from '../utils/tosImage';
import { tosAccelerate } from '../utils/tosAccelerate';
import { creditsToYuan } from '../utils/currency';
import { ScrollingProjectName } from '../components/ScrollingProjectName';
import { HomepageStory } from './homepage/HomepageStory';

function getCurrentUser(): User | null {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

function timeAgo(dateStr: string, lang: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const en = lang.startsWith('en');
  if (mins < 1) return en ? 'just now' : '刚刚';
  if (mins < 60) return en ? `${mins}m ago` : `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return en ? `${hours}h ago` : `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return en ? `${days}d ago` : `${days}天前`;
  const months = Math.floor(days / 30);
  return en ? `${months}mo ago` : `${months}个月前`;
}

const TYPEWRITER_LINES: Record<string, string[]> = {
  zh: [
    'The best way to film with AI',
    'AI 驱动的影视创作新纪元',
    '轻松实现 3 天一部剧',
    '媲美主流影视工业审美的辅助能力',
  ],
  en: [
    'The best way to film with AI',
    'A new era of AI-driven filmmaking',
    'Produce a show in just 3 days',
    'Studio-grade aesthetics, AI-powered',
  ],
};

const LANGUAGES = [
  { code: 'zh', name: '中文' },
  { code: 'en', name: 'English' },
];

export default function Homepage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common', 'login']);
  const [loggedInUser, setLoggedInUser] = useState<User | null>(getCurrentUser());
  const isLoggedIn = !!localStorage.getItem('token') && !!loggedInUser;

  const [twText, setTwText] = useState('');
  const [twLineIdx, setTwLineIdx] = useState(0);
  const [twTyping, setTwTyping] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [projectThumbs, setProjectThumbs] = useState<Record<string, string>>({});
  const [showcaseItems, setShowcaseItems] = useState<ShowcaseItem[]>([]);
  const [showcaseLoading, setShowcaseLoading] = useState(true);
  const [modalVideo, setModalVideo] = useState<ShowcaseItem | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 600px)').matches,
  );
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const heroRef = useRef<HTMLElement>(null);
  const firstScreenRef = useRef<HTMLDivElement>(null);
  const landingRef = useRef<HTMLDivElement>(null);
  const isHeroPagingRef = useRef(false);
  const heroPagingTimerRef = useRef<number | undefined>();

  const langKey = i18n.language.startsWith('en') ? 'en' : 'zh';
  const lines = TYPEWRITER_LINES[langKey];
  const recentProjectLimit = isMobileViewport ? 1 : 9;
  const scrollCueLabel = langKey === 'en' ? 'Learn more' : '了解更多';

  const pageHeroTo = useCallback((top: number) => {
    const hero = heroRef.current;
    if (!hero) return;

    isHeroPagingRef.current = true;
    window.clearTimeout(heroPagingTimerRef.current);
    hero.scrollTo({ top, behavior: 'smooth' });
    heroPagingTimerRef.current = window.setTimeout(() => {
      isHeroPagingRef.current = false;
    }, 700);
  }, []);

  const scrollToStoryHero = useCallback(() => {
    const landing = landingRef.current;
    if (landing) pageHeroTo(landing.offsetTop);
  }, [pageHeroTo]);
  const showcaseSummary = langKey === 'en'
    ? 'Period drama, romance, mystery, and more — let AI become your production team while you remain the director of the story.'
    : '古装、都市、悬疑、浪漫——让 AI 成为你的制作团队，而你始终是故事的导演。';
  const showcaseTitle = langKey === 'en'
    ? <>Every imagination<br />deserves its own screen.</>
    : <>每一种想象，<br />都有自己的银幕。</>;

  useEffect(() => {
    setTwText(''); setTwLineIdx(0); setTwTyping(true);
  }, [langKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 600px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    const line = lines[twLineIdx];
    let timer: ReturnType<typeof setTimeout>;
    if (twTyping) {
      if (twText.length < line.length) {
        timer = setTimeout(() => setTwText(line.slice(0, twText.length + 1)), 80);
      } else {
        timer = setTimeout(() => setTwTyping(false), 2000);
      }
    } else {
      if (twText.length > 0) {
        timer = setTimeout(() => setTwText(twText.slice(0, -1)), 40);
      } else {
        setTwLineIdx((twLineIdx + 1) % lines.length);
        setTwTyping(true);
      }
    }
    return () => clearTimeout(timer);
  }, [twText, twLineIdx, twTyping, lines]);

  useEffect(() => {
    if (isLoggedIn) {
      import('../api').then(({ getCreditsBalance }) =>
        getCreditsBalance().then(r => setCreditBalance(r.credits)).catch(() => {})
      );
    }
  }, [isLoggedIn]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setShowLanguageMenu(false);
  };

  const [loginUser, setLoginUser] = useState('');
  const [loginPwd, setLoginPwd] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const goToProjectManager = () => navigate('/projects');

  const handleLogin = async (e: React.FormEvent, goToProjectsAfterLogin = false) => {
    e.preventDefault();
    if (!loginUser.trim() || !loginPwd.trim()) { setLoginError(t('error.emptyFields', { ns: 'login' })); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      const { login } = await import('../api');
      const res = await login(loginUser.trim(), loginPwd);
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      setLoggedInUser(res.user);
      setLoginUser(''); setLoginPwd('');
      if (goToProjectsAfterLogin) goToProjectManager();
    } catch (err: any) {
      setLoginError(err.message || t('error.loginFailed', { ns: 'login' }));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleStartCreating = () => goToProjectManager();

  useEffect(() => {
    if (!isLoggedIn) return;

    let ignore = false;
    import('../api').then(({ listProjects, getScriptAnalysis }) => {
      listProjects(0).then(r => {
        if (ignore) return;

        const projects = r.projects.slice(0, recentProjectLimit);
        setRecentProjects(projects);
        projects.forEach(p => {
          getScriptAnalysis(p.id).then(analysis => {
            if (ignore) return;
            try {
              const chars = typeof analysis.characters === 'string'
                ? JSON.parse(analysis.characters) : analysis.characters;
              if (!Array.isArray(chars)) return;
              for (const c of chars) {
                if (c.selectedImageUrl) {
                  setProjectThumbs(prev => ({ ...prev, [p.id]: c.selectedImageUrl }));
                  return;
                }
              }
            } catch {}
          }).catch(() => {});
        });
      }).catch(() => {});
    });

    return () => { ignore = true; };
  }, [isLoggedIn, recentProjectLimit]);

  useEffect(() => {
    let ignore = false;
    setShowcaseLoading(true);
    getPublicShowcase(langKey)
      .then(res => { if (!ignore) setShowcaseItems(res.items || []); })
      .catch(() => { if (!ignore) setShowcaseItems([]); })
      .finally(() => { if (!ignore) setShowcaseLoading(false); });
    return () => { ignore = true; };
  }, [langKey]);

  useEffect(() => {
    const hero = heroRef.current;
    const firstScreen = firstScreenRef.current;
    const landing = landingRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!hero || !firstScreen || !landing || reducedMotion.matches) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;

      const delta = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? event.deltaY * 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? event.deltaY * hero.clientHeight
          : event.deltaY;

      if (Math.abs(delta) < 12) return;

      if (isHeroPagingRef.current) {
        event.preventDefault();
        return;
      }

      const firstTop = firstScreen.offsetTop;
      const landingTop = landing.offsetTop;
      const tolerance = 12;

      if (delta > 0 && hero.scrollTop < landingTop - tolerance) {
        event.preventDefault();
        pageHeroTo(landingTop);
      } else if (
        delta < 0
        && hero.scrollTop > firstTop + tolerance
        && hero.scrollTop <= landingTop + tolerance
      ) {
        event.preventDefault();
        pageHeroTo(firstTop);
      }
    };

    hero.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.clearTimeout(heroPagingTimerRef.current);
      hero.removeEventListener('wheel', handleWheel);
    };
  }, [pageHeroTo]);

  const handleCardHover = useCallback((id: string, enter: boolean) => {
    const vid = videoRefs.current.get(id);
    if (!vid) return;
    if (enter) { vid.play().catch(() => {}); }
    else { vid.pause(); vid.currentTime = 0; }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setLoggedInUser(null);
    setRecentProjects([]);
    setProjectThumbs({});
    setCreditBalance(null);
    setShowUserMenu(false);
  };

  return (
    <div className="hp">
      {/* ── Video Background ── */}
      <video className="hp-bg-video" autoPlay muted loop playsInline preload="auto">
        <source src="/720p.mp4" type="video/mp4" />
      </video>
      <div className="hp-bg-overlay" />

      {/* ── Navbar ── */}
      <nav className="hp-nav">
        <div className="hp-nav-inner">
          <Link to="/" className="hp-nav-brand">
            <img src="/VRLOGO.svg" alt="VibeReels" className="hp-nav-icon" />
            <span>VibeReels</span>
          </Link>
          <div className="hp-nav-right">
            {/* 语言切换 */}
            <div style={{ position: 'relative' }}>
              <button
                className="hp-lang-btn"
                onClick={() => { setShowLanguageMenu(v => !v); setShowUserMenu(false); }}
                title={t('language')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </button>
              {showLanguageMenu && (
                <div className="hp-dropdown-menu" onMouseLeave={() => setShowLanguageMenu(false)}>
                  {LANGUAGES.map(lang => (
                    <button key={lang.code} className="hp-dropdown-item" onClick={() => changeLanguage(lang.code)}
                      style={{ fontWeight: i18n.language.startsWith(lang.code) ? 700 : 400,
                        color: i18n.language.startsWith(lang.code) ? 'var(--hp-accent)' : undefined }}>
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isLoggedIn && (
              <div className="hp-user-menu">
                <button className="hp-avatar-btn" onClick={() => { setShowUserMenu(v => !v); setShowLanguageMenu(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>{loggedInUser?.username}</span>
                  {creditBalance !== null && (
                    <span style={{
                      fontSize: '0.75rem',
                      color: creditBalance < 1000 ? '#ff6b6b' : '#2ee8c8',
                      background: creditBalance < 1000 ? 'rgba(255,107,107,0.15)' : 'rgba(46,232,200,0.15)',
                      padding: '0.1rem 0.45rem', borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap',
                    }}>¥{creditsToYuan(creditBalance)}</span>
                  )}
                </button>
                {showUserMenu && (
                  <div className="hp-dropdown-menu" onMouseLeave={() => setShowUserMenu(false)}>
                    <div className="hp-dropdown-balance">
                      <span>{t('balance')}: </span>
                      <strong style={{ color: creditBalance !== null && creditBalance < 1000 ? '#ff6b6b' : '#fff' }}>
                        {creditBalance !== null ? `¥${creditsToYuan(creditBalance)}` : '-'}
                      </strong>
                      <button className="hp-dropdown-detail-btn" onClick={() => { setShowUserMenu(false); navigate('/my-credits'); }}>
                        {t('details')}
                      </button>
                    </div>
                    <button className="hp-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/profile'); }}>{t('profileSettings')}</button>
                    <div className="hp-dropdown-divider" />
                    <button className="hp-dropdown-item" onClick={handleLogout} style={{ color: '#ff6b6b' }}>{t('logout')}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="hp-hero" ref={heroRef}>
        <div className="hp-first-screen" ref={firstScreenRef}>
          <div className="hp-hero-content">
            <img src="/VRLOGO.svg" alt="VibeReels" className="hp-logo" />
            <h1 className="hp-brand-name">VibeReels</h1>
            <p className="hp-slogan">{twText}<span className="hp-cursor">|</span></p>
            {!isLoggedIn && (
              <form className="hp-login-form" onSubmit={handleLogin}>
                <input
                  className="hp-login-input"
                  value={loginUser}
                  onChange={e => setLoginUser(e.target.value)}
                  placeholder={t('usernamePlaceholder', { ns: 'login' })}
                  autoComplete="username"
                />
                <input
                  className="hp-login-input hp-login-input-pwd"
                  type="password"
                  value={loginPwd}
                  onChange={e => setLoginPwd(e.target.value)}
                  placeholder={t('passwordPlaceholder', { ns: 'login' })}
                  autoComplete="current-password"
                />
                {loginError && <p className="hp-login-error">{loginError}</p>}
                <button type="submit" className="hp-btn-primary hp-login-submit" disabled={loginLoading}>
                  {loginLoading ? t('submitting', { ns: 'login' }) : t('submit', { ns: 'login' })}
                </button>
              </form>
            )}
          </div>

          {/* ── Recent Projects ── */}
          {isLoggedIn && (
          <div className="hp-recent">
            <div className="hp-recent-header">
              <h2>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                {t('homepage.recentProjects')}
              </h2>
            </div>
            <div className="hp-recent-body">
              <div className="hp-recent-cards">
                {recentProjects.length === 0 && (
                  <div className="hp-recent-empty">{t('homepage.noProjects')}</div>
                )}
                {recentProjects.map(p => (
                  <div key={p.id} className="hp-project-card" onClick={() => navigate(`/projects/${p.id}/script-summary`)}>
                    <div className="hp-project-thumb">
                      {projectThumbs[p.id] ? (
                        <img src={tosThumbnail(projectThumbs[p.id], 200)} alt={p.name} className="hp-project-thumb-img" />
                      ) : (
                        <img src="/VRLOGO.svg" alt="" className="hp-project-thumb-placeholder" />
                      )}
                    </div>
                    <div className="hp-project-info">
                      <ScrollingProjectName name={p.name} className="hp-project-name" />
                      <span className="hp-project-time">{t('homepage.editedAt', { time: timeAgo(p.updatedAt, i18n.language) })}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button className="hp-recent-manage-btn" onClick={goToProjectManager}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z"/>
                </svg>
                {t('homepage.manageProjects')}
              </button>
            </div>
          </div>
          )}
          <button className="hp-scroll-cue" type="button" onClick={scrollToStoryHero}>
            <span className="hp-scroll-cue-wheel" aria-hidden="true" />
            <span className="hp-scroll-cue-label">{scrollCueLabel}</span>
          </button>
        </div>

        <HomepageStory
          language={langKey}
          onStartCreating={handleStartCreating}
          landingRef={landingRef}
          closingLogin={!isLoggedIn ? (
            <form className="hp-landing-login-form" onSubmit={e => handleLogin(e, true)}>
              <input
                className="hp-login-input"
                value={loginUser}
                onChange={e => setLoginUser(e.target.value)}
                placeholder={t('usernamePlaceholder', { ns: 'login' })}
                autoComplete="username"
              />
              <input
                className="hp-login-input hp-login-input-pwd"
                type="password"
                value={loginPwd}
                onChange={e => setLoginPwd(e.target.value)}
                placeholder={t('passwordPlaceholder', { ns: 'login' })}
                autoComplete="current-password"
              />
              {loginError && <p className="hp-login-error">{loginError}</p>}
              <button type="submit" className="hp-btn-primary hp-login-submit" disabled={loginLoading}>
                {loginLoading ? t('submitting', { ns: 'login' }) : t('submit', { ns: 'login' })}
              </button>
            </form>
          ) : null}
        >
          {/* ── Showcase Gallery ── */}
          {(showcaseItems.length > 0 || showcaseLoading) && (
          <section className="hp-showcase" id="showcase">
            <div className="hp-showcase-inner">
              <div className="hp-showcase-header">
                <div>
                  <span className="hp-showcase-kicker">MADE WITH VIBEREELS</span>
                  <h2 className="hp-showcase-title">{showcaseTitle}</h2>
                </div>
                <p>{showcaseSummary}</p>
              </div>
              <div className="hp-showcase-grid">
                {showcaseLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={`skel-${i}`} className="hp-showcase-card">
                      <div className="hp-showcase-thumb" style={{ background: 'var(--bg-3, #23232b)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </div>
                  ))
                ) : (
                  showcaseItems.map(item => (
                    <div
                      key={item.id}
                      className="hp-showcase-card"
                      onMouseEnter={() => handleCardHover(String(item.id), true)}
                      onMouseLeave={() => handleCardHover(String(item.id), false)}
                      onClick={() => setModalVideo(item)}
                    >
                      <div className="hp-showcase-thumb">
                        <img src={tosAccelerate(item.thumbnailUrl)} alt={item.title} loading="lazy" />
                        <video
                          ref={el => { if (el) videoRefs.current.set(String(item.id), el); }}
                          src={tosAccelerate(item.videoUrl)}
                          muted
                          loop
                          playsInline
                          preload="none"
                          className="hp-showcase-vid"
                        />
                        <div className="hp-showcase-play">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
                            <polygon points="5,3 19,12 5,21"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
          )}
        </HomepageStory>
        {/* ── Footer (Chinese only) ── */}
      {!i18n.language.startsWith('en') && (
        <footer className="hp-footer">
          <span>Copyright© 上海阿吖依文化传媒有限公司版权所有</span>
          <a target="_blank" rel="noopener noreferrer" href="https://beian.miit.gov.cn/">沪ICP备2021036297号-6</a>
          <a target="_blank" rel="noopener noreferrer" href="https://www.beian.gov.cn/portal/registerSystemInfo">
            <img src="/assets/beian.svg" alt="沪公网安备" style={{ width: 16, height: 16 }} />
            沪公网安备 31010102007544号
          </a>
          <a target="_blank" rel="noopener noreferrer" href="https://tsm.miit.gov.cn/dxxzsp/">增值电信业务经营许可证：沪B2-20230361</a>
        </footer>
      )}
      </section>

      {/* ── Video Modal ── */}
      {modalVideo && (
        <div className="hp-modal-overlay" onClick={() => setModalVideo(null)}>
          <div className="hp-modal" onClick={e => e.stopPropagation()}>
            <button className="hp-modal-close" onClick={() => setModalVideo(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <video src={tosAccelerate(modalVideo.videoUrl)} controls autoPlay className="hp-modal-video" />
            <div className="hp-modal-info">
              <span className="hp-modal-title">{modalVideo.title}</span>
              <span className="hp-modal-author">{modalVideo.description}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
